import { useMemo, useState } from "react";
import useVehiclesMutations from "../../data/useVehiclesMutations";
import useVehicleLookupMode from "../../hooks/useVehicleLookupMode";
import { identifierLabel, identifierValidationMessage, isValidIdentifier, normaliseIdentifier, vehicleIdentifierFor } from "../../utils/vehicleIdentifier";
import { IVehicle } from "../../models/Vehicle";
import useNotification from "../notifications/useNotifications";
import { TextInput } from "../shared/Inputs/TextInput";
import { useForm } from "react-hook-form";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import LoadingWheel from "../shared/LoadingWheel";
import useVehicleMatchingParts from "../../data/useVehicleMatchingParts";
import { IPart, IVehicleCategoryPartsFilters, PartStatus } from "../../models/Part";
import openInNewTab from "../../utils/openInNewTab";
import PartVehicleDetails from "../leads/LeadParts/PartVehicleDetails";
import { isStoredVehicle } from "../../utils/vehicleDisplay";

function getYear(date: string | null | undefined) {
  if (!date) return undefined;

  const year = new Date(date).getFullYear();
  return Number.isNaN(year) ? undefined : year;
}

function FindMatchingParts({ vehicle }: { vehicle: IVehicle }) {
  const [showParts, setShowParts] = useState(false);

  const matchingFilters = useMemo<IVehicleCategoryPartsFilters>(() => {
    const fallbackYear = vehicle.dvlaYearOfManufacture ?? undefined;
    const yearFrom = getYear(vehicle.visibilityDate) ?? fallbackYear;
    const yearTo = getYear(vehicle.terminateDate) ?? fallbackYear;
    const partStatuses = [PartStatus.Dismantled, PartStatus.Sold];

    return {
      includeSold: false,
      bodyFilters: {
        matchMake: true,
        matchModel: true,
        matchSeries: false,
        matchMk: false,
        matchBodyStyle: true,
        matchDoors: true,
        matchExactYear: false,
        matchYearRange: true,
        yearFrom,
        yearTo,
        partStatuses,
      },
      mechFilters: {
        matchMake: false,
        matchModel: false,
        matchSeries: false,
        matchFuelType: true,
        matchEngineCode: true,
        matchEngineSize: true,
        matchExactYear: false,
        matchYearRange: true,
        yearFrom,
        yearTo,
        partStatuses,
      },
    };
  }, [vehicle.dvlaYearOfManufacture, vehicle.visibilityDate, vehicle.terminateDate]);

  const { parts } = useVehicleMatchingParts(vehicle.id, matchingFilters, showParts);
  const matchingParts = parts.data?.data ?? [];

  if (!showParts) {
    return (
      <ButtonInput label="Show In Stock Matching Parts" isSubmit={false} onClick={() => setShowParts(true)} classes={undefined} />
    );
  }

  if (parts.isLoading) {
    return (
      <LoadingWheel />
    );
  }

  if (parts.isError) {
    return (
      <div>
        Error loading matching parts
      </div>
    );
  }

  if (matchingParts.length === 0) {
    return (
      <div>
        No in stock parts found
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-lg font-semibold">Matching Stock</h2>
      <table className="min-w-full">
        <thead className="text-left">
          <tr>
            <th>Thumbnail</th>
            <th>Part</th>
            <th>Category</th>
            <th>Tag Number</th>
            <th>Donor VRM</th>
            <th>Type</th>
          </tr>
        </thead>
        <tbody>
          {
            matchingParts.map((part: IPart) => (
              <tr key={part.id} className="cursor-pointer hover:bg-gray-100 border-b" onClick={() => openInNewTab(`/stock/${part.id}`)}>
                <td className="py-2">{part.images.length > 0 ? <img src={part.images[0].url} className="max-w-[5rem]" /> : <p>N/A</p>}</td>
                <td>{part.title}</td>
                <td>{part.category?.name ?? "N/A"}</td>
                <td>{part.tagNumber}</td>
                <td>{part.vehicle?.vrm ?? "N/A"}</td>
                <td>{part.category?.partOnly ? "Body" : "Mechanical"}</td>
              </tr>
            ))
          }
        </tbody>
      </table>
    </div>
  );
};

export default function VehicleSearch() {
  const { vrmLookup, vrmLookupData } = useVehiclesMutations();
  const { identifierType, isVinMode } = useVehicleLookupMode();
  const { addNotification } = useNotification();
  const { register, handleSubmit } = useForm<any>();
  const [vehicle, setVehicle] = useState<IVehicle | undefined>(undefined);
  const [vrm, setVrm] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // The reg being checked is usually a customer's car we will never own, so the lookup that
  // fills this page in must not create it. The details render either way; only the matching-parts
  // search needs a real record, and that asks for one explicitly.
  const onSubmit = (data: any) => {
    const identifier = normaliseIdentifier(data.vrm, identifierType);
    if (!isValidIdentifier(identifier, identifierType)) {
      addNotification({ variant: "error", primaryText: identifierValidationMessage(identifierType) });
      return;
    }

    setIsLoading(true);

    vrmLookupData.mutate(identifier, {
      onSuccess: (response) => {
        if (!response?.data) {
          addNotification({
            variant: "error",
            primaryText: isVinMode ? "No vehicle found for that VIN" : "No vehicle found for that registration",
          });
          setVehicle(undefined);
          return;
        }

        addNotification({
          variant: "success",
          primaryText: "Found vehicle",
        });
        setVehicle(response.data);
      },
      onError: (error) => {
        console.error(error);
        addNotification({
          variant: "error",
          primaryText: isVinMode ? "Error looking up VIN" : "Error looking up VRM",
        });
      },
      onSettled: () => {
        setIsLoading(false);
      }
    });
  }

  // Adds the looked-up vehicle to Wilma so the parts search — which matches on a vehicle id — has
  // something to key on. Explicit, because this is what puts a car in the vehicle list.
  const handleAddToWilma = () => {
    const identifier = vehicle ? vehicleIdentifierFor(vehicle, identifierType) : "";
    if (!identifier) return;

    setIsLoading(true);

    vrmLookup.mutate(identifier, {
      onSuccess: (response) => setVehicle(response.data ?? undefined),
      onError: (error) => {
        console.error(error);
        addNotification({
          variant: "error",
          primaryText: "Error adding vehicle",
        });
      },
      onSettled: () => {
        setIsLoading(false);
      }
    });
  }

  return (
    <div className="px-4">
      <h1 className="mb-2 text-lg font-semibold">Vehicle Lookup</h1>
      <form onSubmit={handleSubmit(onSubmit)} className="mt-2 mb-4 grid grid-cols-1 gap-x-8 gap-y-4 sm:gap-y-4 sm:grid-cols-2">
        <TextInput label={identifierLabel(identifierType)} register={register} registerName="vrm" maxLength={isVinMode ? 17 : undefined} />
        <div className="flex items-center mt-7">
          <ButtonInput label="Find Vehicle" isSubmit={true} classes="w-40 h-10" onClick={() => { }} />
        </div>
      </form>

      {(isLoading && !vehicle) ? (
        <LoadingWheel />
      ) : (
        vehicle && (
          <>
            <div className="pb-6">
              <PartVehicleDetails vehicle={vehicle} showDepollute={false} />
            </div>

            {isStoredVehicle(vehicle) ? (
              <FindMatchingParts vehicle={vehicle} />
            ) : (
              <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
                <p>
                  {isVinMode ? "These are lookup details" : "These are DVLA details"} — this vehicle isn't in Wilma, so there's nothing to match
                  stock against yet.
                </p>
                <div className="mt-2">
                  <ButtonInput
                    label="Add Vehicle To Wilma"
                    isSubmit={false}
                    classes="w-52 h-10"
                    onClick={() => handleAddToWilma()}
                  />
                </div>
              </div>
            )}
          </>
        )
      )}

    </div>
  )
}